import React, { useRef, useEffect } from "react";

/**
 * Lightweight canvas-based matrix rain background.
 * - Respects prefers-reduced-motion
 * - Pauses when the tab is hidden
 * - Capped FPS and column density for perf
 */
export default function MatrixBackground({ opacity = 0.35, density = 1 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const ctx = canvas.getContext("2d");
    let W = (canvas.width = window.innerWidth);
    let H = (canvas.height = window.innerHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 1.6);
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const fontSize = 14;
    const colsCount = Math.floor((W / fontSize) * density);
    const drops = Array.from({ length: colsCount }, () => Math.random() * -H / fontSize);
    const chars = "01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン";

    let last = 0;
    const frameMs = 60; // ~16 fps for a subtle feel + low CPU

    const draw = (ts) => {
      if (ts - last < frameMs) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      last = ts;
      // trail fade
      ctx.fillStyle = "rgba(5, 7, 10, 0.12)";
      ctx.fillRect(0, 0, W, H);

      ctx.font = `${fontSize}px 'JetBrains Mono', monospace`;
      for (let i = 0; i < drops.length; i++) {
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        const ch = chars[(Math.random() * chars.length) | 0];
        // head glyph bright
        ctx.fillStyle = `rgba(0, 240, 255, ${0.78})`;
        ctx.fillText(ch, x, y);
        // trail tint
        ctx.fillStyle = `rgba(0, 102, 255, 0.35)`;
        ctx.fillText(ch, x, y - fontSize);
        if (y > H && Math.random() > 0.975) drops[i] = 0;
        drops[i] += 1;
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    const onResize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.scale(dpr, dpr);
    };
    window.addEventListener("resize", onResize);

    const onVisibility = () => {
      if (document.hidden) cancelAnimationFrame(rafRef.current);
      else rafRef.current = requestAnimationFrame(draw);
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [density]);

  return (
    <>
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10"
        style={{ opacity }}
        data-testid="matrix-background"
      />
      <div aria-hidden="true" className="matrix-floor-wrap"><div className="matrix-floor" /></div>
    </>
  );
}
